import { makeAutoObservable } from "mobx";
import { ZUid } from "src/types/zUid";
import { ZOption } from "src/types/ZOption";
import { onError } from "src/common/onError";
import { ifDef } from "src/common/ifDef";
import { dateValueFormat } from "src/common/formats";
import { loadValuesUserFilter } from "../EntityAttsHistory/apiEntityAttsHistory";
import { AuditFilterJson, AuditFilterUI, ZAuditEvent } from "./ZEntityAudit";
import { loadAudit } from "./apiAudit";

export class EntityAuditStore {
  constructor(public readonly onAuditEvent: (event: ZAuditEvent) => void) {
    makeAutoObservable(this);
  }

  entityId: ZUid = "";

  setEntityId(id: ZUid) {
    this.entityId = id;
  }

  usersList: ZOption[] | null = null;

  setUsersList(list: ZOption[] | null) {
    this.usersList = list;
  }

  usersListLoading = false;

  setUsersListLoading(value: boolean) {
    this.usersListLoading = value;
  }

  async loadUsersList() {
    try {
      this.setUsersListLoading(true);
      const users = await loadValuesUserFilter(this.entityId);
      this.setUsersList(
        users.map(({ userId, fio }) => ({
          value: String(userId),
          label: fio ?? "Не указано",
        })),
      );
    } finally {
      this.setUsersListLoading(false);
    }
  }

  filter: AuditFilterUI = {};

  setFilter(values: AuditFilterUI) {
    this.filter = values;
  }

  get filterJson(): AuditFilterJson {
    const { filter } = this;

    const res: AuditFilterJson = {
      timestampFrom: ifDef(filter.range?.[0], (it) =>
        it.format(dateValueFormat),
      ),
      timestampTo: ifDef(filter.range?.[1], (it) => it.format(dateValueFormat)),
      userId: filter.userId,
    };
    return res;
  }

  async onFilterChange(values: AuditFilterUI) {
    this.setFilter(values);
    return this.update();
  }

  invalidate() {
    this.setEntityId("");
    this.setUsersList(null);
  }

  async init(entityId: ZUid) {
    if (this.entityId !== entityId) {
      this.setEntityId(entityId);
      this.loadUsersList().catch(onError);
      return this.update();
    }
    return Promise.resolve();
  }

  async update() {
    const { entityId } = this;
    if (!entityId) return;
    try {
      this.setLoading(true);
      this.setList([]);
      this.setNextPageToken("");

      const res = await this.loadAuditPage();
      this.setList(res.content);
      this.setNextPageToken(res.pageToken ?? "");
    } finally {
      this.setLoading(false);
    }
  }

  protected async loadAuditPage() {
    const { entityId } = this;
    if (!entityId) throw Error("Expected entityId");
    return loadAudit({
      ...this.filterJson,
      pageSize: 10,
      entityId,
      pageToken: this.nextPageToken || undefined,
    });
  }

  loading = false;

  setLoading(flag: boolean) {
    this.loading = flag;
  }

  list: ZAuditEvent[] = [];

  setList(newList: ZAuditEvent[]) {
    this.list = newList;
  }

  appendList(newItems: ZAuditEvent[]) {
    this.setList([...this.list, ...newItems]);
  }

  nextPageToken: string = "";

  setNextPageToken(value: string) {
    this.nextPageToken = value;
  }

  async loadNextPage() {
    const { nextPageToken, loading } = this;
    if (!nextPageToken || loading) return;
    try {
      this.setLoading(true);
      const res = await this.loadAuditPage();
      this.appendList(res.content);
      this.setNextPageToken(res.pageToken || "");
    } finally {
      this.setLoading(false);
    }
  }
}
